import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { executePerps } from "../../functions/Contract";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { getNibiruSDK } from "../../../config/network";
import { getMarketName } from "../../../utils/marketUtils";
import { getMarketByMarketIndex } from "../../../config/markets";
import TradeResultCard from "../TradeResultCard/TradeResultCard";
import TradeDetailModal from "../Modals/TradeDetailModal";
import { formatPriceWithSignificantDecimals } from "../../../Components/utils/formatter";

const nibiru = getNibiruSDK();

// Updated formatDate function to match TradesRow.js
const formatDate = (timestamp) => {
  try {
    console.log("Original timestamp:", timestamp, "Type:", typeof timestamp);
    
    if (!timestamp) {
      return { date: "Recent", time: "" };
    }

    let date;
    
    // Handle different timestamp formats
    if (typeof timestamp === 'string') {
      // Remove any extra characters and parse
      const cleanTimestamp = timestamp.trim();
      
      if (cleanTimestamp.includes('T') || cleanTimestamp.includes('-')) {
        // ISO string format
        date = new Date(cleanTimestamp);
      } else {
        // Numeric string - try parsing as number
        const numTimestamp = parseInt(cleanTimestamp);
        if (!isNaN(numTimestamp)) {
          // Handle nanosecond timestamps (19 digits) - convert to milliseconds
          if (numTimestamp.toString().length >= 19) {
            // Nanoseconds - divide by 1,000,000 to get milliseconds
            date = new Date(Math.floor(numTimestamp / 1000000));
          } else if (numTimestamp > 1e12) {
            // Already in milliseconds
            date = new Date(numTimestamp);
          } else {
            // Seconds - multiply by 1000 to get milliseconds
            date = new Date(numTimestamp * 1000);
          }
        }
      }
    } else if (typeof timestamp === 'number') {
      // Handle nanosecond timestamps (19 digits) - convert to milliseconds
      if (timestamp.toString().length >= 19) {
        // Nanoseconds - divide by 1,000,000 to get milliseconds
        date = new Date(Math.floor(timestamp / 1000000));
      } else if (timestamp > 1e12) {
        // Already in milliseconds
        date = new Date(timestamp);
      } else {
        // Seconds - multiply by 1000 to get milliseconds
        date = new Date(timestamp * 1000);
      }
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      // Try to convert to string first
      date = new Date(String(timestamp));
    }

    console.log("Parsed date:", date);

    // Validate the date
    if (!date || isNaN(date.getTime())) {
      console.error("Invalid date created from timestamp:", timestamp);
      return { date: "Recent", time: "" };
    }

    // Format the date and time
    const dateStr = date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });

    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    console.log("Formatted result:", { date: dateStr, time: timeStr });
    return { date: dateStr, time: timeStr };

  } catch (error) {
    console.error("Error formatting date:", error, "Original timestamp:", timestamp);
    return { date: "Recent", time: "" };
  }
};

const HistoryRow = ({ trade, onTradeClick, showShareButton = false, showPnlInfo = true, autoOpenDetail = false }) => {
  const currentPrice = useSelector((state) => state.trade.currentPrice);
  const account = useSelector((state) => state.auth.account);
  const dispatch = useDispatch();

  const lowPriceTokenIds = new Set(["pump", "pengu", "bonk", "shib", "nibi"]);

  const getTradeIndexNumber = (userTradeIndex) => {
    if (!userTradeIndex) return "N/A";

    const match = userTradeIndex.toString().match(/\((\d+)\)/);
    return match ? match[1] : userTradeIndex;
  };

  // Format price with currency sign, with subscript notation for low-price tokens
  const formatPrice = (value, isLowPrice) => {
    if (value === null || value === undefined || value === "") return "-";

    const num = parseFloat(value);
    if (isNaN(num)) return "-";

    const abs = Math.abs(num);

    if (isLowPrice && abs > 0 && abs < 1) {
      const raw = abs.toFixed(20);
      const decimalPart = raw.split(".")[1] || "";

      let zeroCount = 0;
      for (let i = 0; i < decimalPart.length; i++) {
        if (decimalPart[i] === "0") {
          zeroCount += 1;
        } else {
          break;
        }
      }

      const remaining = decimalPart.slice(zeroCount).slice(0, 3);

      if (!remaining) {
        return formatPriceWithSignificantDecimals(num, 8, true);
      }

      return (
        <>
          $0.0<sub>{zeroCount}</sub>{remaining}
        </>
      );
    }

    return formatPriceWithSignificantDecimals(num, 8, true);
  };

  // Updated format collateral amount function to return separate amount and currency
  const formatCollateralAmount = (amount, denom = "USDC") => {
    try {
      const numAmount = parseFloat(amount);
      if (isNaN(numAmount)) return { amount: "$0.00", currency: denom };
      
      const formattedAmount = (numAmount / 1000000).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      
      return { amount: `$${formattedAmount}`, currency: denom };
    } catch (error) {
      console.error("Error formatting collateral amount:", error);
      return { amount: "$0.00", currency: denom };
    }
  };

  // Updated format size function to return separate amount and currency
  const formatSizeAmount = (amount, leverage, denom = "USDC") => {
    try {
      const numAmount = parseFloat(amount);
      const numLeverage = parseFloat(leverage);
      
      if (isNaN(numAmount) || isNaN(numLeverage)) return { amount: "$0.00", currency: denom };
      
      const totalSize = (numAmount * numLeverage) / 1000000;
      const formattedSize = totalSize.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      
      return { amount: `$${formattedSize}`, currency: denom };
    } catch (error) {
      console.error("Error formatting size amount:", error);
      return { amount: "$0.00", currency: denom };
    }
  };

  // Format PnL with sign and percentage for closed trades
  const formatPnL = (collateralAmount, openPrice, isLong, leverage, closePrice) => {
    try {
      // Need valid open and close prices
      if (!openPrice || !closePrice || !collateralAmount || !leverage) {
        console.warn("Missing data for PnL calculation:", { 
          openPrice, 
          closePrice,
          collateralAmount,
          leverage,
          tradeId: trade.user_trade_index 
        });
        return { pnl: "0.00", percentage: "0.00", isProfit: false };
      }

      const openPriceNum = parseFloat(openPrice);
      const closePriceNum = parseFloat(closePrice);
      const marginNum = parseFloat(collateralAmount) / (10 ** process.env.REACT_APP_USDC_DECIMALS);
      const leverageNum = parseFloat(leverage);

      if (openPriceNum === 0 || !marginNum || !leverageNum) {
        return { pnl: "0.00", percentage: "0.00", isProfit: false };
      }

      // Position Size = Margin × Leverage
      const positionSize = marginNum * leverageNum;
      
      // Asset Quantity = Position Size ÷ Entry Price
      const assetQuantity = positionSize / openPriceNum;
      
      let pnlUsd;
      let isProfit;

      if (isLong) {
        // Long position: PnL = (Close - Entry) × Quantity
        pnlUsd = (closePriceNum - openPriceNum) * assetQuantity;
        isProfit = closePriceNum > openPriceNum;
      } else {
        // Short position: PnL = (Entry - Close) × Quantity
        pnlUsd = (openPriceNum - closePriceNum) * assetQuantity;
        isProfit = closePriceNum < openPriceNum;
      }

      // Calculate percentage: (PnL / Margin) × 100
      const pnlPercentage = (pnlUsd / marginNum) * 100;

      const absPnlUsd = Math.abs(pnlUsd);

      // Decide decimals for PnL value: keep 2 for normal-sized values, increase for tiny ones
      let pnlDecimals = 2;
      if (absPnlUsd < 1) {
        while (pnlDecimals < 6 && Number(absPnlUsd.toFixed(pnlDecimals)) === 0) {
          pnlDecimals += 1;
        }
      }

      return {
        pnl: absPnlUsd.toLocaleString("en-US", {
          minimumFractionDigits: pnlDecimals,
          maximumFractionDigits: pnlDecimals,
        }),
        percentage: Math.abs(pnlPercentage).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        isProfit: isProfit,
      };
    } catch (error) {
      console.error("Error calculating PnL:", error);
      return { pnl: "0.00", percentage: "0.00", isProfit: false };
    }
  };

  // getMarketName is now imported from marketUtils

  // Format trade side (BUY/SELL)
  const getTradeSide = (isLong) => {
    return isLong ? "BUY" : "SELL";
  };
  
  // Format order type (MARKET/LIMIT)
  const getOrderType = (tradeType) => {
    return tradeType === "limit" ? "LIMIT" : "MARKET";
  };

  // Format leverage with x
  const formatLeverage = (leverage) => {
    if (leverage === undefined || leverage === null) {
      return "1.00x";
    }
    const leverageNum = parseFloat(leverage);
    return isNaN(leverageNum) ? "1.00x" : `${leverageNum.toFixed(2)}x`;
  };

  // Format date with time - same as TradesRow
  const timestamp = trade.trade_info?.last_oi_update_ts || trade.open_timestamp;
  const dateTimeFormatted = timestamp ? formatDate(timestamp) : { date: "Recent", time: "" };

  // Get market pair
  const market = getMarketName(trade.market_index);

  // Detect if this history row is for a low-price token (e.g. PUMP, PENGU, BONK, SHIB, NIBI)
  const marketForHistory = getMarketByMarketIndex(trade.market_index);
  const isLowPriceToken =
    marketForHistory && lowPriceTokenIds.has(marketForHistory.id);

  // Get trade side (BUY/SELL) and order type (MARKET/LIMIT)
  const type = getTradeSide(trade.long);
  const tradeType = getOrderType(trade.trade_type);

  // Format entry price (with subscript for low-price tokens)
  const price = formatPrice(trade.open_price, isLowPriceToken);

  // Format leverage
  const leverage = formatLeverage(trade.leverage);

  // Format position size with separate styling
  const sizeData = formatSizeAmount(trade.collateral_amount, trade.leverage);

  // Format collateral amount with separate styling
  const collateralData = formatCollateralAmount(trade.collateral_amount);

  // Get closing price from trade_info if available, fallback to trade.close_price
  const closePrice = trade.trade_info?.closed_price || trade.close_price;

  // Detect cancelled limit orders (never executed, margin fully returned)
  const closePriceNum = parseFloat(closePrice);
  const isCancelledLimitOrder =
    trade.trade_type === "limit" &&
    !trade.is_open &&
    (!closePrice || isNaN(closePriceNum) || closePriceNum === 0);

  // Format PnL and percentage with the correct close price.
  // For cancelled limit orders, PnL should always be 0.
  const pnlData = isCancelledLimitOrder
    ? { pnl: "0.00", percentage: "0.00", isProfit: false }
    : formatPnL(
        trade.collateral_amount,
        trade.open_price,
        trade.long,
        trade.leverage,
        closePrice
      );

  // Get the formatted closing price (with subscript for low-price tokens)
  const closingPriceFormatted = isCancelledLimitOrder
    ? "-"
    : formatPrice(closePrice, isLowPriceToken);

  // Handle row click
  const handleRowClick = (e) => {
    // Don't navigate if clicking on action buttons (if any)
    if (e.target.closest('button') || e.target.closest('.action-buttons')) {
      return;
    }
    // If parent provides onTradeClick (e.g. AllTradesTable), navigate to market page
    if (onTradeClick) {
      onTradeClick(trade);
      return;
    }
    setDetailModalOpen(true);
  };

  // Get trade index number
  const tradeIndexNumber = getTradeIndexNumber(trade.user_trade_index);

  // State for showing trade result card
  const [showTradeCard, setShowTradeCard] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  useEffect(() => {
    if (autoOpenDetail) setDetailModalOpen(true);
  }, [autoOpenDetail]);

  // Handle share button click
  const handleShareClick = (e) => {
    e.stopPropagation(); // Prevent row click
    setShowTradeCard(true);
  };

  return (
    <>
      {showTradeCard && (
        <TradeResultCard
          trade={trade}
          onClose={() => setShowTradeCard(false)}
        />
      )}
    {/* ─── Mobile: one closed trade per card ─── */}
    <div
      onClick={handleRowClick}
      className="ce-tcard ce-tabular md:hidden"
      style={{ fontFamily: "General Sans" }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-bold truncate" style={{ color: "var(--ink)" }}>
              {market.split('/')[0]}
            </span>
            <span className="text-[11px]" style={{ color: "var(--ink-4)" }}>
              /{market.split('/')[1]}
            </span>
          </div>
          <div className="flex items-center gap-1.5 mt-1.5">
            <span
              className="ce-chip"
              style={{
                height: 20,
                fontSize: 10,
                background: trade.long ? "var(--tint-up)" : "var(--tint-down)",
                color: trade.long ? "var(--on-tint-up)" : "var(--on-tint-down)",
              }}
            >
              {type}
            </span>
            <span className="text-[11px]" style={{ color: "var(--ink-3)" }}>
              {leverage} · {tradeType}
            </span>
            <span className="text-[11px]" style={{ color: "var(--ink-4)" }}>#{tradeIndexNumber}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div
            className="ce-figure text-[16px] font-bold leading-tight"
            style={{ color: pnlData.isProfit ? "var(--up)" : "var(--down)" }}
          >
            ${pnlData.pnl}
          </div>
          <div
            className="ce-figure text-[11.5px] font-medium"
            style={{ color: pnlData.isProfit ? "var(--up)" : "var(--down)" }}
          >
            ({pnlData.percentage}%)
          </div>
        </div>
      </div>

      <div className="ce-tcard-grid">
        <div>
          <span className="ce-tcard-k">Entry</span>
          <span className="ce-tcard-v">{price}</span>
        </div>
        <div>
          <span className="ce-tcard-k">Exit</span>
          <span className="ce-tcard-v">{closingPriceFormatted}</span>
        </div>
        <div>
          <span className="ce-tcard-k">Margin</span>
          <span className="ce-tcard-v">{collateralData.amount}</span>
        </div>
        <div>
          <span className="ce-tcard-k">Size</span>
          <span className="ce-tcard-v">{sizeData.amount}</span>
        </div>
        <div className="col-span-2">
          <span className="ce-tcard-k">Closed</span>
          <span className="ce-tcard-v" style={{ fontSize: 12 }}>
            {dateTimeFormatted.date} {dateTimeFormatted.time}
          </span>
        </div>
      </div>

      {showShareButton && (
        <button
          onClick={handleShareClick}
          className="ce-btn ce-btn-surface w-full text-sm mt-3"
          style={{ height: 38 }}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          Share trade
        </button>
      )}
    </div>

    <div
      onClick={handleRowClick}
      className="ce-row-card ce-tabular hidden md:grid grid-cols-10 cursor-pointer text-sm min-w-[900px] whitespace-nowrap py-2 px-3 sm:px-5"
      style={{ fontFamily: "General Sans", minHeight: 46, marginBottom: 6 }}
    >
      {/* Trade Index */}
      <div className="flex items-center justify-center">
        <span className="text-sm font-medium" style={{ color: "var(--ink)" }}>{tradeIndexNumber}</span>
      </div>

      {/* Date & Time */}
      <div className="flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="text-xs" style={{ color: "var(--ink-3)" }}>{dateTimeFormatted.time}</div>
          <div className="text-sm font-medium" style={{ color: "var(--ink)" }}>{dateTimeFormatted.date}</div>
        </div>
      </div>

      {/* Pair */}
      <div className="flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="font-medium text-sm" style={{ color: "var(--ink)" }}>{market.split('/')[0]}</div>
          <div className="text-xs" style={{ color: "var(--ink-3)" }}>{market.split('/')[1]}</div>
        </div>
      </div>

      {/* Type */}
      <div className="flex items-center justify-center">
        <div className="flex flex-col items-center">
          <span
            className="text-sm font-semibold"
            style={{ color: trade.long ? 'var(--up)' : 'var(--down)' }}
          >
            {type}
          </span>
          <span className="text-xs" style={{ color: "var(--ink-3)" }}>{tradeType}</span>
        </div>
      </div>

      {/* Entry Price */}
      <div className="flex items-center justify-center">
        <span className="text-sm font-medium" style={{ color: "var(--ink)" }}>{price}</span>
      </div>

      {/* Leverage */}
      <div className="flex items-center justify-center">
        <span className="text-sm font-medium" style={{ color: "var(--ink)" }}>{leverage}</span>
      </div>

      {/* Collateral Amount */}
      <div className="flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="text-sm font-medium" style={{ color: "var(--ink)" }}>{collateralData.amount}</div>
          <div className="text-xs" style={{ color: "var(--ink-3)" }}>{collateralData.currency}</div>
        </div>
      </div>

      {/* Size */}
      <div className="flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="text-sm font-medium" style={{ color: "var(--ink)" }}>{sizeData.amount}</div>
          <div className="text-xs" style={{ color: "var(--ink-3)" }}>{sizeData.currency}</div>
        </div>
      </div>

      {/* Exit Price */}
      <div className="flex items-center justify-center">
        <span className="text-sm font-medium" style={{ color: "var(--ink)" }}>{closingPriceFormatted}</span>
      </div>

      {/* PnL */}
      <div className="flex items-center justify-center">
        <div className="flex flex-col items-center gap-1.5">
          <div className="flex flex-col items-center space-y-0.5">
            {/* No glow: weight and hue already carry profit vs loss. */}
            <div
              className="ce-figure text-[14px] font-bold"
              style={{ color: pnlData.isProfit ? 'var(--up)' : 'var(--down)' }}
            >
              ${pnlData.pnl}
            </div>
            <div
              className="ce-figure text-[12px] font-medium"
              style={{ color: pnlData.isProfit ? 'var(--up)' : 'var(--down)' }}
            >
              ({pnlData.percentage}%)
            </div>
          </div>
          {showShareButton && (
            <button
              onClick={handleShareClick}
              className="ce-btn ce-btn-accent text-xs"
              style={{ padding: '4px 12px' }}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              Share
            </button>
          )}
        </div>
      </div>
    </div>
      <TradeDetailModal
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        trade={trade}
      />
    </>
  );
};

export default HistoryRow;